import React, { useState } from "react";

// ----- Helpers -----
const createSeries = (teamA, teamB) => ({
  teamA,
  teamB,
  winsA: 0,
  winsB: 0,
  winner: null,
});

const isFinished = (s) => s.winsA === 4 || s.winsB === 4;

const computeWinner = (s) => {
  if (s.winsA === 4) return s.teamA;
  if (s.winsB === 4) return s.teamB;
  return null;
};

// ----- Components -----
const Series = ({ series, onWin }) => {
  return (
    <div className="bg-white shadow-md rounded-2xl p-3 w-52">
      <button
        className="flex justify-between w-full"
        onClick={() => onWin("A")}
        disabled={isFinished(series)}
      >
        <span>{series.teamA || "TBD"}</span>
        <span>{series.winsA}</span>
      </button>

      <button
        className="flex justify-between w-full"
        onClick={() => onWin("B")}
        disabled={isFinished(series)}
      >
        <span>{series.teamB || "TBD"}</span>
        <span>{series.winsB}</span>
      </button>

      <div className="mt-2 text-xs text-gray-500">Best of 7</div>
    </div>
  );
};

const Round = ({ title, series, onWin }) => (
  <div
    className="flex flex-col gap-6"
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <h3 className="text-center font-bold">{title}</h3>
    {series.map((s, i) => (
      <Series key={i} series={s} onWin={(side) => onWin(i, side)} />
    ))}
  </div>
);

const Team = ({ TeamName, gamesPlayed, win, lost }) => (
  <div style={{}}>
    <div>{TeamName}</div>
    <div>
      <div></div>
    </div>
  </div>
);

// ----- Main -----
export default function Bracket() {
  const [east, setEast] = useState({
    r1: [
      createSeries("E1", "E8"),
      createSeries("E4", "E5"),
      createSeries("E2", "E7"),
      createSeries("E3", "E6"),
    ],
    r2: [createSeries(null, null), createSeries(null, null)],
    r3: [createSeries(null, null)],
  });

  const [west, setWest] = useState({
    r1: [
      createSeries("W1", "W8"),
      createSeries("W4", "W5"),
      createSeries("W2", "W7"),
      createSeries("W3", "W6"),
    ],
    r2: [createSeries(null, null), createSeries(null, null)],
    r3: [createSeries(null, null)],
  });

  const [final, setFinal] = useState(createSeries(null, null));

  // ----- Logic propagation -----
  const handleWin = (conf, round, index, side) => {
    const update = conf === "east" ? { ...east } : { ...west };
    const r = [...update[round]];
    const s = { ...r[index] };

    if (side === "A") s.winsA++;
    else s.winsB++;

    s.winner = computeWinner(s);
    r[index] = s;
    update[round] = r;

    // propagate winner
    if (s.winner) {
      if (round === "r1") {
        const next = [...update.r2];
        const target = Math.floor(index / 2);
        const nextSeries = { ...next[target] };

        if (index % 2 === 0) nextSeries.teamA = s.winner;
        else nextSeries.teamB = s.winner;

        next[target] = nextSeries;
        update.r2 = next;
      }

      if (round === "r2") {
        const next = [...update.r3];
        const nextSeries = { ...next[0] };

        if (index === 0) nextSeries.teamA = s.winner;
        else nextSeries.teamB = s.winner;

        next[0] = nextSeries;
        update.r3 = next;
      }

      if (round === "r3") {
        const next = { ...final };

        if (conf === "east") next.teamA = s.winner;
        else next.teamB = s.winner;

        setFinal(next);
      }
    }

    conf === "east" ? setEast(update) : setWest(update);
  };

  const handleFinalWin = (side) => {
    const s = { ...final };
    if (side === "A") s.winsA++;
    else s.winsB++;
    s.winner = computeWinner(s);
    setFinal(s);
  };

  return (
    <div className="wrapper">
      <div className="main">
        <div>
          <div
            className="flex"
            style={{ display: "flex", flexDirection: "row", gap: "2rem" }}
          >
            <Round
              title="East - Round 1"
              series={east.r1}
              onWin={(i, side) => handleWin("east", "r1", i, side)}
            />
            <Round
              title="Round 2"
              series={east.r2}
              onWin={(i, side) => handleWin("east", "r2", i, side)}
            />
            <Round
              title="Conference Final"
              series={east.r3}
              onWin={(i, side) => handleWin("east", "r3", i, side)}
            />
          </div>

          {/* FINAL */}
          <div className="bg-yellow-100 p-4 rounded-2xl shadow-lg">
            <div className="text-center font-bold mb-2">Stanley Cup Final</div>
            <Series series={final} onWin={handleFinalWin} />
            {final.winner && (
              <div className="text-center mt-2 font-bold">
                🏆 {final.winner}
              </div>
            )}
          </div>
          {/* WEST */}
          <div
            className="flex"
            style={{ display: "flex", flexDirection: "row", gap: "2rem" }}
          >
            <Round
              title="Conference Final"
              series={west.r3}
              onWin={(i, side) => handleWin("west", "r3", i, side)}
            />
            <Round
              title="Round 2"
              series={west.r2}
              onWin={(i, side) => handleWin("west", "r2", i, side)}
            />
            <Round
              title="West - Round 1"
              series={west.r1}
              onWin={(i, side) => handleWin("west", "r1", i, side)}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div className="conference">
            <div className="division">
              <div className="atlantic">
                <div></div>
                <div></div>
              </div>
              <div className="metropolitan">
                <div></div>
                <div></div>
              </div>
            </div>
            <div className="division2round"></div>
            <div className="finaldivision"></div>
          </div>
          <div className="finalStanleyCup"></div>
          <div className="conference">
            <div className="division">
              <div className="pacific">
                <div></div>
                <div></div>
              </div>
              <div className="central">
                <div></div>
                <div></div>
              </div>
            </div>
            <div className="division2round"></div>
            <div className="finaldivision"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
